export const MAX_IMAGE_DIMENSION = 500;

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read image"));
    };
    img.src = url;
  });
}

function canvasToBlob(canvas, mime, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Could not process image"))),
      mime,
      quality,
    );
  });
}

function outputName(file, mime) {
  const base = (file.name || "image").replace(/\.[^.]+$/, "") || "image";
  const ext = mime === "image/png" ? ".png" : mime === "image/webp" ? ".webp" : ".jpg";
  return `${base}${ext}`;
}

function pickMime(file) {
  if (file.type === "image/png") return "image/png";
  if (file.type === "image/webp") return "image/webp";
  return "image/jpeg";
}

/**
 * Resize so neither width nor height exceeds maxDimension (proportional).
 * Returns the original file when both sides are already within the limit.
 */
export async function resizeImageFile(file, maxDimension = MAX_IMAGE_DIMENSION, maxBytes = null) {
  if (!file?.type?.startsWith("image/")) {
    return file;
  }

  const img = await loadImage(file);
  const { width, height } = img;

  if (width <= maxDimension && height <= maxDimension && (!maxBytes || file.size <= maxBytes)) {
    return file;
  }

  const scale = Math.min(1, maxDimension / Math.max(width, height));
  const targetW = Math.max(1, Math.round(width * scale));
  const targetH = Math.max(1, Math.round(height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, targetW, targetH);

  let mime = pickMime(file);
  let quality = mime === "image/jpeg" ? 0.9 : undefined;
  let blob = await canvasToBlob(canvas, mime, quality);

  if (maxBytes && blob.size > maxBytes && mime !== "image/jpeg") {
    mime = "image/jpeg";
    quality = 0.9;
    blob = await canvasToBlob(canvas, mime, quality);
  }

  while (maxBytes && blob.size > maxBytes && mime === "image/jpeg" && quality > 0.5) {
    quality -= 0.1;
    blob = await canvasToBlob(canvas, mime, quality);
  }

  return new File([blob], outputName(file, mime), { type: mime, lastModified: Date.now() });
}
